import React from 'react';

import cs from 'classnames';

import {FormControlGroup} from 'app/general/forms';
import {ACCOUNT_OVERVIEW_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {DueDateInputField} from './DueDateInputField';
import {DueTimeInputField} from './DueTimeInputField';
import {useTaskStartTime} from './use-task-start-time';

import type {unitOfTime} from 'moment-tz';

interface Props {
  due: string;
  onDueChange: (due: string) => unknown;
  dtstart: string | null | undefined;
  onDtstartChange: (dtstart: string) => unknown;
  force_dtstart?: boolean;
  onForceDtstartChange: (force_dtstart: boolean) => unknown;
  prependToDueDateForm?: React.ReactNode;
  lockDueDate: boolean;
  userTimezone: string;
  userLanguage: string;
}

export function DueDateForm({
  due,
  onDueChange,
  dtstart,
  onDtstartChange,
  force_dtstart,
  onForceDtstartChange,
  prependToDueDateForm,
  lockDueDate,
  userTimezone: tz,
  userLanguage,
}: Props): JSX.Element {
  const {
    duration,
    setDuration,
    durationTypes,
    isStartTimeFormVisible,
    setStartTimeFormVisible,
  } = useTaskStartTime(due, dtstart, onDtstartChange);

  return (
    <>
      {prependToDueDateForm ?? null}
      <div className="form-row align-items-end">
        <FormControlGroup className="col-md-4" field="due">
          <label>{gt('Due date')}</label>
          <DueDateInputField
            due={due}
            onChange={onDueChange}
            lockDueDate={lockDueDate}
            tz={tz}
          />
        </FormControlGroup>
        <FormControlGroup className="col-md-4" field="time">
          <label>{gt('Due time')}</label>
          <DueTimeInputField
            dueDate={due}
            onChange={onDueChange}
            userLanguage={userLanguage}
            tz={tz}
          />
        </FormControlGroup>
        {!isStartTimeFormVisible ? (
          <div className="col-md-4 form-group">
            <button
              className="btn btn-link"
              onClick={() => setStartTimeFormVisible(true)}
            >
              {gt('Add start time')}
            </button>
          </div>
        ) : null}
      </div>

      <p className="alert alert-light small p-2">
        {gt('Enter the date and time this task is due in your timezone')}{' '}
        <a className="alert-link" href={ACCOUNT_OVERVIEW_URL}>
          ({tz})
        </a>
        .
      </p>

      {isStartTimeFormVisible ? (
        <div className="form-row">
          <FormControlGroup className="col-12 form-inline" field="dtstart">
            {(invalidFeedback, errorClass) => (
              <>
                <label className="justify-content-start">
                  {gt(
                    'This task starts %(value_field)s %(valuetype_field)s before it is due.',
                    {
                      value_field: (
                        <input
                          className={cs('form-control col-2 mx-2', errorClass)}
                          type="number"
                          value={duration.value}
                          onChange={(e) =>
                            setDuration({
                              value: parseInt(e.target.value),
                            })
                          }
                          placeholder="0"
                        />
                      ),
                      valuetype_field: (
                        <select
                          className={cs('form-control mr-2', errorClass)}
                          value={duration.type}
                          onChange={(e) =>
                            setDuration({
                              type: e.target.value as unitOfTime.Diff,
                            })
                          }
                        >
                          {durationTypes.map((u) => (
                            <option key={u.key} value={u.key}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                      ),
                    }
                  )}
                </label>
                {invalidFeedback}
              </>
            )}
          </FormControlGroup>
          <FormControlGroup
            className="col-12 form-inline form-check"
            field="force_dtstart"
          >
            <label className="form-check-label" htmlFor="forceStartDateId">
              <input
                className="form-check-input"
                type="checkbox"
                name="force_dtstart"
                onChange={() => onForceDtstartChange(!force_dtstart)}
                checked={force_dtstart}
                id="forceStartDateId"
              />
              {gt("Assignees can't complete the task before this start date")}
            </label>
          </FormControlGroup>
        </div>
      ) : null}
    </>
  );
}
